import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box, Button, Box as FormActions, Box as FormFields } from '@node-space/ui-components'
import {
  contactActionAtom,
  contactAtom,
  contactInstrumentActionAtom,
} from 'pages/Contacts/atoms/contactAtoms'
import { useFiatInstrumentForm } from 'pages/Contacts/hooks/useFiatInstrumentForm'
import { ContactFormKeys } from 'pages/Contacts/schemas/contactSchema'
import { Steps } from 'pages/Contacts/types/fiatInstruments'
import {
  getFieldsToValidate,
  getRequiredContactFieldsForBankAccount,
  populateInstrumentFormWithBeneficiaryDetails,
} from 'pages/Contacts/utils/fiatInstruments'
import { ContactActionIds } from 'types/atoms/atom.contacts'
import {
  BeneficiaryDetails,
  BeneficiaryDetailsCurrency,
  BeneficiaryTransferDestination,
  PaymentMethod,
  PaymentMethods,
} from 'types/beneficiaries'
import { ContactCard } from '../ContactCard'
import { ContactsSheets } from '../ContactsSheets'
import { BankAccountBeneficiaryDetails } from './BankAccountBeneficiaryDetails'
import { BankAccountDetails } from './BankAccountDetails'
import { CurrencyDetails } from './CurrencyDetails'
import { FiatInstrumentConfirmation } from './FiatInstrumentConfirmation'
import { IntermediaryBankDetails } from './IntermediaryBankDetails'

const NAVIGATION_BACK_FLOW = {
  [Steps.BANK_DETAILS]: Steps.CURRENCY_DETAILS,
  [Steps.BENEFICIARY_DETAILS]: Steps.BANK_DETAILS,
  [Steps.CONFIRMATION]: Steps.BENEFICIARY_DETAILS,
}

export const ContactFiatInstrumentForm = () => {
  const { t } = useTranslation()

  const contact = useAtomValue(contactAtom)
  const [contactInstrumentAction, setContactInstrumentAction] = useAtom(contactInstrumentActionAtom)
  const setContact = useSetAtom(contactAtom)
  const [contactAction, setContactAction] = useAtom(contactActionAtom)

  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)

  const { meta } = contactAction || {}
  const { contactBeneficiary } = meta || {}

  // Used to also get the latest contact value in the useCallback functions
  const contactRef = useRef(contact)
  contactRef.current = contact

  const {
    errors,
    formFieldLabels,
    formValues,
    form,
    isBusinessBankAccount,
    isInternationalBankAccount,
    step,
    isEditingContactInstrument,
    clearErrors,
    register,
    setStep,
    setValue,
    trigger,
    setError,
  } = useFiatInstrumentForm()

  const openContactUpdateSheetIfRequired = useCallback(
    (requiredFieldsForContactUpdate: ContactFormKeys[]) => {
      setContactAction({
        ...contactAction,
        actionId: ContactActionIds.CONTACT,
        header: t('contacts.updateContact'),
        onClose: () => {
          setIsContactSheetOpen(false)
          contactAction?.onClose?.()
        },
        meta: {
          keepUpdatedContact: true,
          contactRequiredFields: {
            requiredFields: requiredFieldsForContactUpdate,
            calloutMessage: t('contacts.bankAccountRequiredAdditionalContactDetails'),
          },
        },
      })
      setIsContactSheetOpen(true)
    },
    [contactAction, t]
  )

  useEffect(() => {
    if (contactBeneficiary) {
      handleContactBeneficiaryPaymentMethodsSelect(contactBeneficiary)
      setValue('transferDestination', contactBeneficiary?.transferDestination)
      populateInstrumentFormWithBeneficiaryDetails(contactBeneficiary, setValue)
    }
  }, [contactBeneficiary, setValue])

  const handleContactBeneficiaryPaymentMethodsSelect = useCallback(
    (contactBeneficiary: BeneficiaryDetails) => {
      const isSwiftBeneficiary =
        contactBeneficiary?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL

      switch (contactBeneficiary?.currency) {
        case BeneficiaryDetailsCurrency.USD:
          setValue('paymentMethods', [contactBeneficiary?.paymentMethod])
          break
        case BeneficiaryDetailsCurrency.EUR:
          setValue(
            'paymentMethods',
            isSwiftBeneficiary
              ? [PaymentMethods.SWIFT, PaymentMethods.SEPA, PaymentMethods.SEPA_INSTANT]
              : [PaymentMethods.SEPA, PaymentMethods.SEPA_INSTANT]
          )
          break
        case BeneficiaryDetailsCurrency.GBP:
          setValue(
            'paymentMethods',
            isSwiftBeneficiary
              ? [PaymentMethods.SWIFT, PaymentMethods.FASTER_PAYMENT]
              : [PaymentMethods.FASTER_PAYMENT]
          )
          break
        case BeneficiaryDetailsCurrency.AED:
          setValue('paymentMethods', [PaymentMethods.SWIFT])
          break
        case BeneficiaryDetailsCurrency.ZAR:
        default:
          setValue('paymentMethods', [])
          break
      }
    },
    [setValue]
  )

  const onContinue = useCallback(async () => {
    const currentFormValues = form?.getValues()

    const { paymentMethods } = currentFormValues
    const currentContact = contactRef.current  
    const fieldsToValidate = getFieldsToValidate(step, currentFormValues)
    const isValid = await trigger(fieldsToValidate)

    if (!isValid) {
      return
    }

    const handleCurrencyDetailsStep = () => {
      const requiredFieldsForContactUpdate = getRequiredContactFieldsForBankAccount(
        currentContact,
        paymentMethods as PaymentMethod[]
      )

      if (requiredFieldsForContactUpdate.length) {
        openContactUpdateSheetIfRequired(requiredFieldsForContactUpdate)
        return
      }

      setStep(Steps.BANK_DETAILS)
    }

    switch (step) {
      case Steps.CURRENCY_DETAILS:
        handleCurrencyDetailsStep()
        break
      case Steps.BANK_DETAILS:
        setStep(Steps.BENEFICIARY_DETAILS)
        break
      case Steps.BENEFICIARY_DETAILS:
        setStep(Steps.CONFIRMATION)
        break
    }
  }, [
    contactAction,
    contactInstrumentAction,
    form,
    formValues?.isIntermediaryBankDetailsRequired,
    step,
    setContact,
    setContactAction,
    setStep,
    t,
    trigger,
    openContactUpdateSheetIfRequired,
  ])

  const actions = useMemo(() => {
    switch (step) {
      case Steps.CURRENCY_DETAILS:
      case Steps.BENEFICIARY_DETAILS:
      case Steps.BANK_DETAILS:
        return (
          <FormActions flex justify="between" paddingT={20}>
            <Button
              testId="cancel"
              type="button"
              variant="secondary"
              onClick={() => {
                contactInstrumentAction?.onClose()
              }}
            >
              {t('cancel')}
            </Button>
            <Button testId="continue" type="button" variant="primary" onClick={onContinue}>
              {t('continue')}
            </Button>
          </FormActions>
        )
      case Steps.CONFIRMATION:
      case Steps.SUCCESS:
      default:
        return
    }
  }, [step])

  const handleGoBack = () => {
    // Cannot go back from the first step
    if (step === Steps.CURRENCY_DETAILS) {
      return
    }

    const previousStep = NAVIGATION_BACK_FLOW[step]

    if (previousStep) {
      setStep(previousStep)
    }
  }

  const canGoBack = useMemo(() => {
    return (
      step === Steps.CONFIRMATION ||
      step === Steps.BANK_DETAILS ||
      step === Steps.BENEFICIARY_DETAILS
    )
  }, [step])

  const subHeader = useMemo(() => {
    switch (step) {
      case Steps.CURRENCY_DETAILS:
        return
      case Steps.BENEFICIARY_DETAILS:
        return t('contacts.beneficiaryDetails')
      case Steps.BANK_DETAILS:
        return t('contacts.bankAccountDetails')
      case Steps.CONFIRMATION:
        return t('contacts.confirmBankAccountDetails')
      default:
        return
    }
  }, [step, t])

  useEffect(() => {
    setContactInstrumentAction({
      ...contactInstrumentAction,
      header: isEditingContactInstrument ? t('contacts.updateBankAccount') : t('contacts.addBankAccount'),
      subHeader: {
        subTitle: subHeader,
      },
      onBack: canGoBack ? handleGoBack : null,
    })
  }, [step, canGoBack, subHeader])

  return (
    <Box width="full">
      <form data-testid="contact-bank-account-form">
        <FormFields width="full" flexCol gapY={16} paddingT={24}>
          {/* Step 1: Basic account details */}
          {step === Steps.CURRENCY_DETAILS && (
            <Box paddingB={8}>
              <ContactCard name={contact?.name} title={t('contacts.contact')} showAvatar={false} />
              <CurrencyDetails
                formValues={formValues}
                errors={errors}
                isInternationalBankAccount={isInternationalBankAccount}
                clearErrors={clearErrors}
                setValue={setValue}
                register={register}
                contactBeneficiary={contactBeneficiary}
              />
            </Box>
          )}

           {/* Step 2: Bank account details */}
           {step === Steps.BANK_DETAILS && (
            <Box paddingB={8}>
              <ContactCard name={contact?.name} title={t('contacts.contact')} showAvatar={false} />
              <BankAccountDetails
                formFieldLabels={formFieldLabels}
                formValues={formValues}
                isBusinessAccount={isBusinessBankAccount}
                isInternationalBankAccount={isInternationalBankAccount}
                setValue={setValue}
                clearErrors={clearErrors}
                trigger={trigger}
                setError={setError}
                register={register}
                errors={errors}
                contactBeneficiary={contactBeneficiary}
              />

              {formValues?.isIntermediaryBankDetailsRequired && (
                <IntermediaryBankDetails
                  formValues={formValues}
                  errors={errors}
                  clearErrors={clearErrors}
                  register={register}
                  setError={setError}
                  setValue={setValue}
                  trigger={trigger}
                  contactBeneficiary={contactBeneficiary}
                />
              )}
            </Box>
          )}

          {/* Step 3: Beneficiary details */}
          {step === Steps.BENEFICIARY_DETAILS && (
            <Box paddingB={8}>
              <ContactCard name={contact?.name} title={t('contacts.contact')} showAvatar={false} />
              <BankAccountBeneficiaryDetails
                formFieldLabels={formFieldLabels}
                formValues={formValues}
                errors={errors}
                isBusinessAccount={isBusinessBankAccount}
                isInternationalBankAccount={isInternationalBankAccount}
                clearErrors={clearErrors}
                setValue={setValue}
                register={register}
                setError={setError}
                setIsContactSheetOpen={setIsContactSheetOpen}
                contactBeneficiary={contactBeneficiary}
              />
            </Box>
          )}

          {/* Step 4: Confirmation */}
          {step === Steps.CONFIRMATION && (
            <Box paddingB={12}>
              <ContactCard name={contact?.name} title={t('contacts.contact')} showAvatar={false} />
              <FiatInstrumentConfirmation
                formValues={formValues}
                formFieldLabels={formFieldLabels}
                form={form}
                isLoading={false}
              />
            </Box>
          )}
        </FormFields>

        {actions}
      </form>
      <ContactsSheets isSheetOpen={isContactSheetOpen} isNestedSheet />
    </Box>
  )
}
